/**
 * The one loader -- the only module in the client allowed to know how asset
 * bytes arrive (docs/ARCH-PLAN.md §3.1, held by scripts/gates/client-boundary.mjs).
 *
 * Callers name an asset by id ("cat/ginger", "cat/toon.clips") and get bytes or
 * parsed JSON back; the manifest beside this file maps the id to whatever
 * the build encoded for its target -- a hashed relative URL on the web and
 * desktop faces, a data: URI in the artifact.  fetch() consumes both
 * identically, which is the whole trick: one code path, no `if (artifact)`
 * anywhere in game code, ever.  The trap this file exists to avoid is the
 * old one-per-consumer decode helper (three copies of an atob loop, base64
 * string + decoded buffer + GPU copy all resident at once); GLTFLoader.parse
 * and decodeAudioData both take the ArrayBuffer straight from here.
 *
 * The loader stays deliberately dumb: no cache, no retries, no progress.
 * Boot awaits each asset once and hands the result to something that keeps
 * it; a cache here would be a second copy of every model, which is the
 * residency bug this file replaces.
 */
import { ASSETS } from "./manifest.js";

/** The manifest row for an id, or a loud throw -- an unknown id is a build
    catalog slip (packages/client/build.mjs owns the id table), and "failed
    to fetch undefined" is the kind of error that costs an evening.
    @param {string} id */
export function urlFor(id) {
  const url = ASSETS[id];
  if (!url) {
    throw new Error(`unknown asset id "${id}" -- not in the build manifest ` +
      `(${Object.keys(ASSETS).length} id(s) present). The catalog lives in ` +
      `packages/client/build.mjs; a page built any other way has an empty manifest.`);
  }
  return url;
}

/** @param {string} id  @param {string} kind */
async function get(id, kind) {
  const url = urlFor(id);
  const res = await fetch(url);
  if (!res.ok) {
    // data: URIs cannot 404; this is the web face telling us a hashed file
    // is missing behind the manifest -- a deploy that shipped the page
    // without its assets directory.
    throw new Error(`asset "${id}" (${kind}): ${res.status} ${res.statusText} for ${url}`);
  }
  return res;
}

/** The bytes of one asset.  @param {string} id  @returns {Promise<ArrayBuffer>} */
export async function loadBytes(id) {
  return (await get(id, "bytes")).arrayBuffer();
}

/** One JSON asset, parsed.  @param {string} id  @returns {Promise<object>} */
export async function loadJson(id) {
  return (await get(id, "json")).json();
}
